import { Mic, Send, Settings2, Volume2 } from "lucide-react";
import FeedbackPanel from "@/components/lesson/FeedbackPanel";

function getMicrophoneLabel({ isTranscribing, isListening }) {
  if (isTranscribing) return "Transcrevendo...";
  if (isListening) return "Parar e transcrever";
  return "Gravar voz";
}

function MessageItem({ message, index }) {
  return (
    <article className={`chat-bubble ${message.role === "user" ? "chat-user" : "chat-ai"}`} data-testid={`lesson-message-${message.role}-${index}`}>
      <p data-testid={`lesson-message-text-${message.role}-${index}`}>{message.text}</p>
      {message.tip && <small data-testid={`lesson-message-tip-${index}`}>Dica: {message.tip}</small>}
      {message.score && <small data-testid={`lesson-message-score-${index}`}>Pontuação: {message.score}%</small>}
    </article>
  );
}

function MessageList({ finalFeedback, isComplete, messages }) {
  return (
    <div className="chat-stream" data-testid="lesson-message-stream">
      {messages.map((message, index) => (
        <MessageItem key={`${message.id}-${index}`} message={message} index={index} />
      ))}
      {isComplete && <FeedbackPanel finalFeedback={finalFeedback} />}
    </div>
  );
}

function ResponseComposer({
  draft,
  handleMicrophoneClick,
  handleMicrophoneTest,
  isComplete,
  isListening,
  isTestingMicrophone,
  isTranscribing,
  microphoneDisabled,
  setDraft,
  speechStatusText,
  submitTurn,
  turnPending,
}) {
  return (
    <div className="chat-composer" data-testid="lesson-response-composer">
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Fale pelo microfone ou digite sua resposta em inglês..."
        className="response-input"
        data-testid="lesson-response-textarea"
        disabled={isComplete}
      />
      <div className="mic-test-panel" data-testid="lesson-microphone-test-panel">
        <div>
          <strong data-testid="lesson-microphone-test-title">Antes de falar</strong>
          <span data-testid="lesson-microphone-test-copy">Se for sua primeira vez, teste o microfone aqui.</span>
        </div>
        <button type="button" className="secondary-action w-auto" onClick={handleMicrophoneTest} disabled={microphoneDisabled || isListening || isTestingMicrophone} data-testid="lesson-test-microphone-button">
          <Settings2 size={18} /> {isTestingMicrophone ? "Testando..." : "Testar microfone"}
        </button>
      </div>
      <div className="flex flex-wrap gap-3">
        <button type="button" className={`secondary-action ${isListening ? "listening" : ""}`} onClick={handleMicrophoneClick} disabled={microphoneDisabled} data-testid="lesson-microphone-button" data-testgroup="lesson-mic-control">
          <Mic size={18} /> {getMicrophoneLabel({ isTranscribing, isListening })}
        </button>
        <button type="button" className="primary-action w-auto" onClick={submitTurn} disabled={!draft.trim() || isComplete || turnPending} data-testid="lesson-send-response-button">
          Enviar resposta <Send size={18} />
        </button>
      </div>
      <p className="text-sm font-bold text-white/70" data-testid="speech-status-message">{speechStatusText}</p>
    </div>
  );
}

export default function ConversationPanel({
  currentPrompt,
  draft,
  finalFeedback,
  handleMicrophoneClick,
  handleMicrophoneTest,
  isComplete,
  isListening,
  isTestingMicrophone,
  isTranscribing,
  messages,
  setDraft,
  speak,
  speechRecognitionAvailable,
  speechStatus,
  submitTurn,
  supportsAudioRecording,
  turnPending,
}) {
  const microphoneDisabled = (!supportsAudioRecording && !speechRecognitionAvailable) || isComplete || isTranscribing;
  const defaultSpeechStatus = supportsAudioRecording
    ? "Dica: clique em gravar, fale uma frase curta em inglês e clique em parar para transcrever com Whisper."
    : "Seu navegador não liberou gravação de voz. Digite sua resposta para praticar.";

  return (
    <section className="lesson-chat" data-testid="lesson-chat-panel">
      <div className="chat-header" data-testid="lesson-chat-header">
        <div>
          <p className="section-kicker" data-testid="lesson-chat-kicker">simulação ao vivo</p>
          <h2 className="font-display text-2xl font-black" data-testid="lesson-chat-title">Responda como se estivesse lá</h2>
        </div>
        <button type="button" className="icon-action" onClick={() => speak(currentPrompt)} data-testid="repeat-ai-voice-button" aria-label="Repetir áudio da IA">
          <Volume2 size={20} />
        </button>
      </div>
      <MessageList finalFeedback={finalFeedback} isComplete={isComplete} messages={messages} />
      <ResponseComposer
        draft={draft}
        handleMicrophoneClick={handleMicrophoneClick}
        handleMicrophoneTest={handleMicrophoneTest}
        isComplete={isComplete}
        isListening={isListening}
        isTestingMicrophone={isTestingMicrophone}
        isTranscribing={isTranscribing}
        microphoneDisabled={microphoneDisabled}
        setDraft={setDraft}
        speechStatusText={speechStatus || defaultSpeechStatus}
        submitTurn={submitTurn}
        turnPending={turnPending}
      />
    </section>
  );
}